import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Sparkles,
} from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';
import { getServiceLabel } from '@/lib/services';

interface Slide {
  titleKey:
    | 'casablanca'
    | 'rabat'
    | 'epilation'
    | 'packminceurcomplet'
    | 'hydrafacial'
    | 'skincare';

  subtitle: Record<'fr' | 'ar' | 'en', string>;
  gradient: string;
  image?: string;
  imageSrcSet?: string;
  imageSizes?: string;
}

const SLIDES: Slide[] = [
  {
    titleKey: 'packminceurcomplet',
    subtitle: {
      fr: 'Perdre 6 kg et 5 cm de tour de taille en 1 MOIS',
      ar: 'فقدان 6 كجم و5 سم من محيط الخصر خلال شهر واحد',
      en: 'Lose 6 kg and 5 cm in waist in 1 month',
    },
    gradient: 'from-[#2C2C2C] via-black to-[#D4AF37]/30',
    image: '/images/minceur.webp',
    imageSrcSet: '/images/minceur-672.webp 672w, /images/minceur-768.webp 768w, /images/minceur-1024.webp 1024w, /images/minceur-1344.webp 1344w, /images/minceur.webp 1774w',
    // Account for object-cover: the 2:1 image must also cover the slide height.
    imageSizes: '(min-width: 1633px) 1600px, (min-width: 784px) 98vw, (min-width: 640px) 768px, 672px',
  },

  {
    titleKey: 'epilation',
    subtitle: {
      fr: 'Éliminez définitivement les poils indésirables en 6 SÉANCES',
      ar: 'بشرة ناعمة ونقية تدوم طويلاً.',
      en: 'Smooth, clear skin that lasts.',
    },
    gradient: 'from-[#2C2C2C] via-black to-[#D4AF37]/30',
    image: '/images/Slider_1920x960.webp',
  },

  {
    titleKey: 'skincare',
    subtitle: {
      fr: 'Résultat rapide & efficace en 1 SÉANCE',
      ar: 'بشرة نقية ونضرة ومشرقة.',
      en: 'Clear, fresh and radiant skin.',
    },
    gradient: 'from-[#2C2C2C] via-black to-[#D4AF37]/30',
    image: '/images/soinVisage.webp',
  },
];

const CITY_LABELS: Record<
  'casablanca' | 'rabat',
  Record<'fr' | 'ar' | 'en', string>
> = {
  casablanca: {
    fr: 'Casablanca',
    ar: 'الدار البيضاء',
    en: 'Casablanca',
  },

  rabat: {
    fr: 'Rabat',
    ar: 'الرباط',
    en: 'Rabat',
  },
};

const SKINCARE_LABELS: Record<
  'fr' | 'ar' | 'en',
  string
> = {
  fr: 'SOINS DU VISAGE',
  ar: 'العناية بالبشرة',
  en: 'SKINCARE',
};

export const ImageSlider = forwardRef<HTMLDivElement>(
  function ImageSlider(_props, ref) {
    const { language } = useLanguage();

    const [activeIndex, setActiveIndex] = useState(0);

    /**
     * True while user is interacting with
     * a Before / After comparison.
     */
    const [isBeforeAfterInteracting, setIsBeforeAfterInteracting] =
      useState(false);

    const activeSlide = SLIDES[activeIndex];

    /**
     * Listen to Before / After interaction events.
     *
     * Your BeforeAfter component will dispatch:
     *
     * before-after-interaction-start
     * before-after-interaction-end
     */
    useEffect(() => {
      const handleInteractionStart = () => {
        setIsBeforeAfterInteracting(true);
      };

      const handleInteractionEnd = () => {
        setIsBeforeAfterInteracting(false);
      };

      window.addEventListener(
        'before-after-interaction-start',
        handleInteractionStart
      );

      window.addEventListener(
        'before-after-interaction-end',
        handleInteractionEnd
      );

      return () => {
        window.removeEventListener(
          'before-after-interaction-start',
          handleInteractionStart
        );

        window.removeEventListener(
          'before-after-interaction-end',
          handleInteractionEnd
        );
      };
    }, []);

    /**
     * Autoplay.
     *
     * Important:
     * while a Before / After card is being used,
     * NO timer exists.
     *
     * When interaction stops,
     * a completely fresh timer starts.
     */
    useEffect(() => {
      if (isBeforeAfterInteracting) {
        return;
      }

      const delay =
        activeIndex === 0
          ? 7000
          : 3000;

      const timeout = window.setTimeout(() => {
        setActiveIndex(
          (current) =>
            (current + 1) % SLIDES.length
        );
      }, delay);

      return () => {
        window.clearTimeout(timeout);
      };
    }, [
      activeIndex,
      isBeforeAfterInteracting,
    ]);

    /**
     * Preload next image.
     */
    useEffect(() => {
      const nextIndex =
        (activeIndex + 1) % SLIDES.length;

      const nextSlide = SLIDES[nextIndex];
      const nextImage = nextSlide?.image;

      if (!nextImage) {
        return;
      }

      // Let the visible images finish before spending bandwidth on the next slide.
      let timer: number | undefined;
      const preload = () => {
        timer = window.setTimeout(() => {
          const image = new Image();
          image.decoding = 'async';
          image.fetchPriority = 'low';
          if (nextSlide.imageSizes) image.sizes = nextSlide.imageSizes;
          if (nextSlide.imageSrcSet) image.srcset = nextSlide.imageSrcSet;
          image.src = nextImage;
        }, 1000);
      };
      if (document.readyState === 'complete') preload();
      else window.addEventListener('load', preload, { once: true });

      return () => {
        window.removeEventListener('load', preload);
        window.clearTimeout(timer);
      };
    }, [activeIndex]);

    const goTo = useCallback(
      (index: number) => {
        if (SLIDES.length === 0) {
          return;
        }

        const nextIndex =
          ((index % SLIDES.length) +
            SLIDES.length) %
          SLIDES.length;

        setActiveIndex(nextIndex);
      },
      []
    );

    const title = useMemo(() => {
      if (
        activeSlide.titleKey === 'skincare'
      ) {
        return SKINCARE_LABELS[language];
      }

      if (
        activeSlide.titleKey === 'casablanca' ||
        activeSlide.titleKey === 'rabat'
      ) {
        return CITY_LABELS[
          activeSlide.titleKey
        ][language];
      }

      return getServiceLabel(
        activeSlide.titleKey,
        language
      );
    }, [
      activeSlide.titleKey,
      language,
    ]);

    return (
      <div
        ref={ref}
        className="
          sticky
          top-24
          z-0
          w-[min(98vw,1600px)]
          mx-[calc(50%-min(49vw,800px))]
          rounded-2xl
          overflow-hidden
          shadow-2xl
          border
          border-[#D4AF37]/40
        "
      >
        <div className="relative h-[21rem] sm:h-96">
          {SLIDES.map((slide, index) => (
            <div
              key={
                slide.image ??
                slide.titleKey
              }
              className={`
                absolute
                inset-0
                flex
                flex-col
                items-center
                justify-center
                text-center
                px-6
                bg-gradient-to-br
                ${slide.gradient}
                transition-opacity
                duration-500
                ease-in-out

                ${
                  index === activeIndex
                    ? 'opacity-100'
                    : 'opacity-0 pointer-events-none'
                }
              `}
            >
              {slide.image && index === activeIndex && (
                <img
                  src={slide.image}
                  srcSet={slide.imageSrcSet}
                  sizes={slide.imageSizes}
                  alt=""
                  width={1600}
                  height={768}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  decoding={index === 0 ? 'sync' : 'async'}
                  draggable={false}
                  className={`
                    absolute
                    inset-0
                    h-full
                    w-full
                    object-cover

                    ${
                      slide.titleKey ===
                      'skincare'
                        ? `
                          object-[100%_center]
                          sm:scale-[1.18]
                          sm:origin-right
                        `
                        : 'object-center'
                    }
                  `}
                />
              )}

              <div
                className="
                  absolute
                  inset-0
                  bg-black/20
                  pointer-events-none
                "
              />

              {/* GOOGLE REVIEWS */}
              <div
                aria-label="Google review score 4.9 out of 5"
                className={`
                  absolute
                  z-20
                  flex
                  items-center
                  gap-2
                  rounded-[8px]
                  border
                  border-[#D4AF37]/40
                  bg-black/30
                  px-2
                  py-1
                  shadow-lg

                  sm:bg-white/10
                  sm:backdrop-blur-[2px]

                  ${
                    slide.titleKey ===
                    'packminceurcomplet'
                      ? `
                        left-1/2
                        bottom-5
                        -translate-x-1/2
                        sm:bottom-6
                        sm:px-3
                      `
                      : `
                        left-4
                        bottom-5
                        sm:left-5
                        sm:bottom-6
                        sm:px-3
                      `
                  }
                `}
              >
                <img
                  src="/images/optimized/ui/googleAvis.webp"
                  alt="Google logo"
                  width={32}
                  height={32}
                  decoding="async"
                  className="
                    h-7
                    w-7
                    object-contain
                    sm:h-8
                    sm:w-8
                  "
                />

                <div
                  className="
                    flex
                    flex-col
                    items-start
                    text-white
                  "
                >
                  <div
                    className="
                      flex
                      items-baseline
                      gap-1
                    "
                  >
                    <span
                      className="
                        text-[0.8rem]
                        font-bold
                        leading-none
                        tracking-tight
                        sm:text-[1.1rem]
                      "
                    >
                      4.9/
                    </span>

                    <div
                      className="
                        flex
                        items-center
                        gap-[1px]
                        text-[#fbbf24]
                      "
                    >
                      {[0, 1, 2, 3, 4].map(
                        (i) => (
                          <span
                            key={i}
                            className="
                              text-[0.5rem]
                              leading-none
                              sm:text-[0.7rem]
                            "
                          >
                            ★
                          </span>
                        )
                      )}
                    </div>
                  </div>

                  <div
                    className="
                      mt-0.5
                      text-[0.42rem]
                      font-semibold
                      leading-none
                      text-white/90
                      sm:text-[0.54rem]
                    "
                  >
                    300+ 5 stars
                  </div>
                </div>
              </div>

              {/* CONTENT */}
              <div
                className={`
                  relative
                  z-10
                  flex
                  flex-col
                  gap-2
                  text-white

                  ${
                    slide.titleKey ===
                    'epilation'
                      ? `
                        left-[-24%]
                        items-center
                        text-center
                        sm:left-[-30%]
                        sm:max-w-[32%]
                      `
                      : slide.titleKey ===
                          'skincare'
                        ? `
                          left-[-24%]
                          items-center
                          text-center
                          sm:left-[-30%]
                          sm:max-w-[45%]
                        `
                        : `
                          items-center
                          text-center
                        `
                  }
                `}
              >
                {slide.titleKey ===
                  'casablanca' ||
                slide.titleKey ===
                  'rabat' ? (
                  <MapPin
                    size={28}
                    className="text-[#D4AF37]"
                  />
                ) : (
                  <Sparkles
                    size={28}
                    className="text-[#D4AF37]"
                  />
                )}

                <h3
                  className="
                    text-[1.4rem]
                    font-bold
                    sm:text-[1.8rem]
                  "
                  style={{
                    fontFamily:
                      "'Playfair Display', serif",
                  }}
                >
                  {getSlideTitle(
                    slide,
                    language
                  )}
                </h3>

                <p
                  className={`
                    ${
                      slide.titleKey ===
                      'skincare'
                        ? `
                          max-w-[20rem]
                          sm:max-w-[24rem]
                        `
                        : `
                          max-w-[14rem]
                          sm:max-w-[15rem]
                        `
                    }

                    text-[0.72rem]
                    leading-relaxed
                    text-gray-200
                    sm:text-[0.9rem]
                  `}
                >
                  {slide.titleKey ===
                    'packminceurcomplet' &&
                  language === 'fr' ? (
                    <>
                      Perdre{' '}
                      <span className="font-bold text-white">
                        6 kg et 5 cm
                      </span>{' '}
                      de tour de taille
                      <br />

                      <span
                        className="
                          text-[0.8rem]
                          font-bold
                          text-white
                          sm:text-[1.05rem]
                        "
                      >
                        en 1 MOIS
                      </span>
                    </>
                  ) : slide.titleKey ===
                      'skincare' &&
                    language === 'fr' ? (
                    <>
                      Résultat rapide & efficace{' '}

                      <span
                        className="
                          text-[0.8rem]
                          font-bold
                          text-white
                          sm:text-[1.05rem]
                        "
                      >
                        en 1 SÉANCE
                      </span>
                    </>
                  ) : slide.titleKey ===
                      'epilation' &&
                    language === 'fr' ? (
                    <>
                      Éliminez définitivement
                      les poils indésirables{' '}

                      <span
                        className="
                          text-[0.8rem]
                          font-bold
                          text-white
                          sm:text-[1.05rem]
                        "
                      >
                        en 6 SÉANCES
                      </span>
                    </>
                  ) : (
                    slide.subtitle[language]
                  )}
                </p>
              </div>
            </div>
          ))}

          {/* PREVIOUS */}
          <button
            type="button"
            onClick={() =>
              goTo(activeIndex - 1)
            }
            aria-label="Previous"
            className="
              absolute
              left-2
              top-1/2
              -translate-y-1/2
              z-20
              h-9
              w-9
              flex
              items-center
              justify-center
              rounded-full
              bg-black/40
              text-white
              hover:bg-[#D4AF37]
              hover:text-black
              transition
            "
          >
            <ChevronLeft size={20} />
          </button>

          {/* NEXT */}
          <button
            type="button"
            onClick={() =>
              goTo(activeIndex + 1)
            }
            aria-label="Next"
            className="
              absolute
              right-2
              top-1/2
              -translate-y-1/2
              z-20
              h-9
              w-9
              flex
              items-center
              justify-center
              rounded-full
              bg-black/40
              text-white
              hover:bg-[#D4AF37]
              hover:text-black
              transition
            "
          >
            <ChevronRight size={20} />
          </button>
        </div>

      </div>
    );
  }
);

/**
 * Separate helper so every rendered slide
 * can obtain its own title.
 */
function getSlideTitle(
  slide: Slide,
  language: 'fr' | 'ar' | 'en'
) {
  if (
    slide.titleKey === 'skincare'
  ) {
    return SKINCARE_LABELS[language];
  }

  if (
    slide.titleKey === 'casablanca' ||
    slide.titleKey === 'rabat'
  ) {
    return CITY_LABELS[
      slide.titleKey
    ][language];
  }

  return getServiceLabel(
    slide.titleKey,
    language
  );
}
